import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { LogItem } from '@/services/email-dispute/logs/index.d';
import { getEmailDisputeLogs } from '@/services/email-dispute/logs/index';
import { useParams } from 'umi';
import { useLocalStorageState } from 'ahooks';


export const LOCAL_EMAIL_LOGS_OPEN = 'email_logs_open';
// Context 类型定义
interface LogsContextType {
  // 模块是否打开
  isLogOpen: boolean;
  // 日志数据
  logItems: LogItem[];
  loading: boolean;

  refreshLogs: () => Promise<void>;
  initializeLogs: (ticketId: number) => Promise<void>;
  setIsLogOpen: (open: boolean) => void;
}

// 创建 Context
const LogsContext = createContext<LogsContextType | undefined>(undefined);

// Provider Props
interface LogsProviderProps {
  children: ReactNode;
  pageSize?: number; // 每页数量，默认为 20
}

// Provider 组件
export const LogsProvider: React.FC<LogsProviderProps> = ({
  children,
}) => {
  const [isLogOpen, setIsLogOpen] = useLocalStorageState(LOCAL_EMAIL_LOGS_OPEN,{ defaultValue: false });
  const [logItems, setLogItems] = useState<LogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { id = '' } = useParams();
  const ticketId = Number(id);

  // 加载日志数据
  const loadLogs = useCallback(async (
    ticketId: number,
  ) => {
    if (loading) return;

    setLoading(true);

    try {
      const data = await getEmailDisputeLogs(ticketId);
      setLogItems(data)
    } catch (error) {
      console.error('加载日志失败:', error);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // 初始化日志（首次加载）
  const initializeLogs = useCallback(async (TicketId: number) => {
    setLogItems([]);
    await loadLogs(TicketId);
  }, [loadLogs]);


  // 刷新日志
  const refreshLogs = useCallback(async () => {
    if (!ticketId) return;
    await loadLogs(ticketId);
  }, [ticketId]);

  const contextValue: LogsContextType = {
    isLogOpen,
    setIsLogOpen,
    logItems,
    loading,
    refreshLogs,
    initializeLogs,
  };
  useEffect(() => {
    if (isLogOpen) {
      initializeLogs(ticketId);
    }
  }, [isLogOpen]);

  return (
    <LogsContext.Provider value={contextValue}>
      {children}
    </LogsContext.Provider>
  );
};

// 自定义 Hook
export const useLogs = (): LogsContextType => {
  const context = useContext(LogsContext);
  if (context === undefined) {
    throw new Error('useLogs must be used within a LogsProvider');
  }
  return context;
};

export { LogsContext };
